import React, { useContext, useEffect } from 'react';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { format } from 'date-fns';
import { Box, Button, Typography } from '@mui/material';
import { useUserRolePermissions } from 'hooks';

import { ChainOffRounded, ChainRounded } from 'library';
import SettingsBackupRestoreRoundedIcon from '@mui/icons-material/SettingsBackupRestoreRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import FilterTiltShiftRoundedIcon from '@mui/icons-material/FilterTiltShiftRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CallMergeRoundedIcon from '@mui/icons-material/CallMergeRounded';
import { DrawerContext } from 'common/drawer/DrawerContext';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

// Components
import { DrawerLayoutWrapper, MAlert } from 'common';
import { DRAWER_TYPES } from 'utils/constants';
import { useTheme } from '@mui/material/styles';
import { FUTURE_TRANSFER_DATE_MSG, PTO_DIALOGUE_TYPES, PTO_STATUS } from '../utils';
import StatusChip from '../status-chip/StatusChip';
import CreatePtoWizard from '../create-wizard/CreateWizard.connector';
// eslint-disable-next-line import/no-cycle
import EditPtoPanel from '../edit-panel/EditPanel.connector';

// Styles
import {
  contentStyles,
  isIconStyles,
  listBoxStyles,
  locationNameStyles,
  sectionDivStyles,
  sectionTitleStyles,
} from './ViewPanel.styles';

const ViewPtoPanel = ({
  data,
  isLoading,
  onClose,
  fetchPtoDetails,
  setCurrentPtoDialogueOpen,
  resetCreatePto,
  ptoNumber: ptoNum,
}) => {
  const theme = useTheme();
  const { setDrawerComponentProps } = useContext(DrawerContext);

  const {
    affiliateIsOriginator,
    affiliateIsRecipient,
    chainList,
    custody,
    dataCompany,
    errorMessage,
    inventorySelection,
    inventorySelectionMessage,
    location,
    origin,
    originMessage,
    originator,
    originList,
    product,
    ptoNumber,
    recipient,
    reconsignmentControl,
    reconsignmentControlMessage,
    referenceId,
    status,
    transferDate,
    volume,
  } = data;

  const { isProductOwnerManage } = useUserRolePermissions();

  useEffect(() => {
    fetchPtoDetails({
      ProductTransferOrderId: ptoNum,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ptoNum]);

  const onCopy = () =>
    setDrawerComponentProps({
      Component: props => <CreatePtoWizard copyPtoNumber={ptoNumber} isCopyToNew {...props} />,
      resetWizard: resetCreatePto,
      type: DRAWER_TYPES.copyPto,
    });

  const onEdit = () =>
    setDrawerComponentProps({
      Component: props => <EditPtoPanel isEdit ptoNumber={ptoNumber} {...props} />,
      resetWizard: resetCreatePto,
      type: DRAWER_TYPES.editPto,
    });

  const openPtoDialog = type => {
    setCurrentPtoDialogueOpen({
      ptoDialogueType: type,
      ptoNumber,
    });
  };

  const nConfirmedActionsRecipients = () => (
    <>
      <Button fullWidth onClick={() => openPtoDialog(PTO_DIALOGUE_TYPES.reject)} variant="outlined">
        Reject Order
      </Button>
      <Button
        fullWidth
        onClick={() => openPtoDialog(PTO_DIALOGUE_TYPES.accept)}
        variant="contained"
      >
        Accept Order
      </Button>
    </>
  );

  const nConfirmedActionsOriginators = () => (
    <>
      <Button
        className="css-heap-redact"
        fullWidth
        onClick={() => openPtoDialog(PTO_DIALOGUE_TYPES.cancel)}
        variant="outlined"
      >
        Cancel Order
      </Button>
      <Button className="css-heap-redact" fullWidth onClick={onCopy}>
        Copy Order
      </Button>
    </>
  );

  const pendingOrApprovedActions = () =>
    affiliateIsOriginator && (
      <>
        <Button
          fullWidth
          onClick={() => openPtoDialog(PTO_DIALOGUE_TYPES.cancel)}
          variant="outlined"
        >
          Cancel Order
        </Button>

        <Button fullWidth onClick={onCopy}>
          Copy Order
        </Button>
      </>
    );

  const rejectedActions = () =>
    affiliateIsOriginator && (
      <>
        <Button
          fullWidth
          onClick={() => openPtoDialog(PTO_DIALOGUE_TYPES.cancel)}
          variant="outlined"
        >
          Cancel Order
        </Button>

        <Button fullWidth onClick={onEdit} variant="contained">
          Edit Order
        </Button>
      </>
    );

  const cancelledActions = () =>
    affiliateIsOriginator && (
      <>
        <Button fullWidth onClick={onCopy}>
          Copy Order
        </Button>
      </>
    );

  let actionButtons;

  if (status === PTO_STATUS.approvalNeeded && affiliateIsRecipient)
    actionButtons = nConfirmedActionsRecipients();
  else if (status === PTO_STATUS.pendingApproval && affiliateIsOriginator)
    actionButtons = nConfirmedActionsOriginators();
  else if (status === PTO_STATUS.rejected) actionButtons = rejectedActions();
  else if (status === PTO_STATUS.approved) actionButtons = pendingOrApprovedActions();
  else if (status === PTO_STATUS.cancelled) actionButtons = cancelledActions();
  else if (status === PTO_STATUS.scheduled) actionButtons = pendingOrApprovedActions();

  const inventorySelectionIcon = () => {
    switch (inventorySelection) {
      case 'AVAILABLE':
        return <SettingsBackupRestoreRoundedIcon sx={{ ...isIconStyles(theme) }} />;
      case 'INTRANSIT':
        return <HistoryRoundedIcon sx={{ ...isIconStyles(theme) }} />;
      case 'EITHER':
        return <FilterTiltShiftRoundedIcon sx={{ ...isIconStyles(theme) }} />;
      default:
        return null;
    }
  };

  const reconsignmentControlIcon = () => {
    const lockIconType =
      reconsignmentControl === 'CONTROL' ? (
        <LockOutlinedIcon sx={{ ...isIconStyles(theme) }} />
      ) : (
        <LockOpenRoundedIcon sx={{ ...isIconStyles(theme) }} />
      );

    const iconType =
      reconsignmentControl === 'EITHER' ? (
        <FilterTiltShiftRoundedIcon sx={{ ...isIconStyles(theme) }} />
      ) : (
        lockIconType
      );

    return iconType;
  };

  const originIcon = () => {
    switch (origin) {
      case 'Custom':
        return <CallMergeRoundedIcon sx={{ ...isIconStyles(theme) }} />;
      case 'Any':
        return <FilterTiltShiftRoundedIcon sx={{ ...isIconStyles(theme) }} />;
      default:
        return null;
    }
  };

  const custodyIcon = () => {
    switch (custody) {
      case 'Direct':
        return <ChainOffRounded sx={{ ...isIconStyles(theme) }} />;
      case 'Chained':
        return <ChainRounded sx={{ ...isIconStyles(theme) }} />;
      default:
        return null;
    }
  };

  return (
    <DrawerLayoutWrapper
      buttons={isProductOwnerManage && actionButtons}
      headerButtonText="CLOSE"
      isLoading={isLoading}
      onHeaderButtonClick={onClose}
      title="View Order"
    >
      <Box display="flex" flexDirection="column" sx={{ ...contentStyles(theme) }}>
        <Box sx={{ ...sectionDivStyles(theme) }}>
          <Box display="flex" flexDirection="column" mb={3}>
            <Typography
              className="css-heap-redact"
              sx={{ ...sectionTitleStyles(theme) }}
              variant="caption"
            >
              Originator
            </Typography>
            <Typography
              className="css-heap-redact"
              color="textPrimary"
              component="span"
              variant="body1"
            >
              {originator.replace(' -', '')}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" mb={3}>
            <Typography
              className="css-heap-redact"
              sx={{ ...sectionTitleStyles(theme) }}
              variant="caption"
            >
              Recipient
            </Typography>
            <Typography
              className="css-heap-redact"
              color="textPrimary"
              component="span"
              variant="body1"
            >
              {recipient.replace(' -', '')}
            </Typography>
          </Box>
          <Box alignItems="center" display="flex" justifyContent="space-between">
            <StatusChip status={status} />
            <Typography variant="body2">{dataCompany}</Typography>
          </Box>
        </Box>
        <Box sx={{ ...sectionDivStyles(theme) }}>
          <Box display="flex" flexDirection="column">
            <Typography sx={{ ...sectionTitleStyles(theme) }} variant="caption">
              Location
            </Typography>
            <Typography color="textPrimary" sx={{ ...locationNameStyles(theme) }} variant="body2">
              {location.replace(' -', '')}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography className="css-heap-redact" variant="caption">
              Product
            </Typography>
            <Typography className="css-heap-redact" color="textPrimary" variant="body2">
              {`${volume}`} {`${product}`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ...sectionDivStyles(theme) }}>
          <Box display="flex" flexDirection="column" mb={3}>
            <Typography sx={{ ...sectionTitleStyles(theme) }} variant="caption">
              Transfer Date
            </Typography>
            <Typography color="textPrimary" variant="caption">
              {transferDate && format(new Date(transferDate), 'MMMM d, yyyy')}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" mb={3}>
            <Typography sx={{ ...sectionTitleStyles(theme) }} variant="caption">
              PTO Number
            </Typography>
            <Typography color="textPrimary" variant="caption">
              {ptoNumber}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography sx={{ ...sectionTitleStyles(theme) }} variant="caption">
              Reference ID
            </Typography>
            <Typography color="textPrimary" variant="caption">
              {`${referenceId || '---'}`}
            </Typography>
          </Box>
        </Box>
        <Box mt={3}>
          <Box alignItems="center" display="flex" mb={2}>
            {inventorySelectionIcon()}
            <Typography color="textPrimary" variant="caption">
              {inventorySelectionMessage}
            </Typography>
          </Box>
          <Box alignItems="center" display="flex" mb={2}>
            {reconsignmentControlIcon()}
            <Typography color="textPrimary" variant="caption">
              {reconsignmentControlMessage}
            </Typography>
          </Box>
          <Box alignItems="center" display="flex" mb={2}>
            {originIcon()}
            <Typography color="textPrimary" variant="caption">
              {originMessage}
            </Typography>
          </Box>
          <Box alignItems="center" display="flex" mb={3}>
            {custodyIcon()}
            <Typography color="textPrimary" variant="caption">
              {custody}
            </Typography>
          </Box>
        </Box>
        {chainList?.length > 0 && (!affiliateIsRecipient || affiliateIsOriginator) && (
          <Box display="flex" flexDirection="column" pt={1.5} sx={{ ...listBoxStyles(theme) }}>
            <Typography mb={0.5} variant="caption">
              Customer Chain
            </Typography>
            {chainList.map(chain => (
              <Typography color="textPrimary" key={chain} variant="body2">
                {chain}
              </Typography>
            ))}
          </Box>
        )}
        {originList?.length > 0 && (
          <Box
            display="flex"
            flexDirection="column"
            pb={1.5}
            pt={1.5}
            sx={{ ...listBoxStyles(theme) }}
          >
            <Typography mb={0.5} variant="caption">
              Origin
            </Typography>
            {originList.map(orig => (
              <Typography color="textPrimary" key={orig} p={0} variant="body2">
                {orig}
              </Typography>
            ))}
          </Box>
        )}
        <Box pb={1}>
          {errorMessage && status !== PTO_STATUS.scheduled && (
            <MAlert message={errorMessage} severity="error" sx={{ mt: 1 }} />
          )}
          {status === PTO_STATUS.scheduled && (
            <MAlert
              iconType={<InfoRoundedIcon sx={{ color: 'graphic.main', margin: 'auto' }} />}
              message={FUTURE_TRANSFER_DATE_MSG}
              severity="info"
              sx={{
                bgcolor: 'success.lighter',
                color: 'text.secondary',
                mb: 2,
              }}
              variant="standard"
            />
          )}
        </Box>
      </Box>
    </DrawerLayoutWrapper>
  );
};

ViewPtoPanel.propTypes = {
  data: shape({
    affiliateIsOriginator: bool.isRequired,
    affiliateIsRecipient: bool.isRequired,
    chainList: arrayOf(string).isRequired,
    custody: string.isRequired,
    dataCompany: string.isRequired,
    errorMessage: string,
    inventorySelection: string.isRequired,
    inventorySelectionMessage: string,
    location: string.isRequired,
    origin: string.isRequired,
    originList: arrayOf(string).isRequired,
    originMessage: string,
    originator: string.isRequired,
    product: string.isRequired,
    ptoNumber: number,
    recipient: string.isRequired,
    reconsignmentControl: string.isRequired,
    reconsignmentControlMessage: string,
    referenceId: string,
    status: string.isRequired,
    transferDate: string.isRequired,
    volume: string.isRequired,
  }).isRequired,
  fetchPtoDetails: func.isRequired,
  isLoading: bool.isRequired,
  onClose: func.isRequired,
  ptoNumber: number,
  resetCreatePto: func.isRequired,
  setCurrentPtoDialogueOpen: func.isRequired,
};

ViewPtoPanel.defaultProps = {
  ptoNumber: null,
};

export default ViewPtoPanel;
